/**
 * ig-form-mailer
 * ---------------------------------------------------------------------------
 * Transporte de formularios hacia el mailer propio (una Lambda), que es lo que
 * permite que cada formulario del sitio llegue a un buzon distinto: el form de
 * contacto nativo de Shopify entrega siempre al unico mail de contacto de la
 * tienda y no acepta destinatario desde el tema.
 *
 * Es la generalizacion de los dos custom elements que el tema publicado tiene
 * hardcodeados (`wholesale-form` y `contact-locals-form`): mismo contrato de
 * red —POST JSON de `Object.fromEntries(new FormData(form))`, respuesta
 * `{ success: boolean }`— pero con el endpoint como atributo, asi que una
 * seccion nueva se suma sin tocar este archivo.
 *
 *   <ig-form-mailer data-endpoint="https://…/mailer/wholesale"
 *                   data-success="…" data-error="…">
 *     <form novalidate> … <div data-mailer-status hidden></div> … </form>
 *   </ig-form-mailer>
 *
 * El `<form>` va con `novalidate`: la validacion la hace este archivo para
 * poder marcar los campos con la misma clase que usa el tema publicado
 * (`has-error`) en vez de depender de los globos del navegador.
 */
(function () {
  'use strict';

  if (customElements.get('ig-form-mailer')) return;

  var ERROR_CLASS = 'has-error';

  class IGFormMailer extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.form.dataset.mailerBound === 'true') return;

      this.form.dataset.mailerBound = 'true';
      this.status = this.querySelector('[data-mailer-status]');
      this.submitBtn = this.querySelector('button[type="submit"]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    get endpoint() {
      return (this.dataset.endpoint || '').trim();
    }

    fields() {
      return Array.from(this.form.querySelectorAll('input, select, textarea')).filter(function (el) {
        return el.type !== 'hidden' && el.type !== 'submit';
      });
    }

    /**
     * El criterio es el del tema publicado: `required`, o un asterisco en el
     * placeholder o en el label — los disenios marcan el campo obligatorio con
     * el asterisco y no siempre llega el atributo.
     */
    isRequired(el) {
      if (el.required) return true;
      if ((el.placeholder || '').indexOf('*') !== -1) return true;
      var label = el.id ? this.querySelector('label[for="' + CSS.escape(el.id) + '"]') : null;
      return !!(label && label.textContent.indexOf('*') !== -1);
    }

    validate() {
      var self = this;
      var ok = true;
      var first = null;

      this.fields().forEach(function (el) {
        var value = (el.value || '').trim();
        var bad = false;

        if (self.isRequired(el) && value === '') bad = true;

        // El CUIT entra sin guiones ni espacios. Es la unica regla de formato
        // que el tema publicado valida aparte del requerido, y se conserva.
        if (!bad && el.name === 'cuit' && value !== '' && !/^\d+$/.test(value)) bad = true;

        if (!bad && el.type === 'email' && value !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) bad = true;

        el.classList.toggle(ERROR_CLASS, bad);
        if (bad) {
          ok = false;
          if (!first) first = el;
        }
      });

      if (first) {
        first.focus({ preventScroll: true });
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return ok;
    }

    setStatus(message, isError) {
      if (!this.status) return;
      this.status.hidden = false;
      this.status.textContent = message;
      this.status.setAttribute('role', isError ? 'alert' : 'status');
      this.status.classList.toggle('is-error', !!isError);
      this.status.classList.toggle('is-success', !isError);
    }

    async onSubmit(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.endpoint) {
        // Sin endpoint no hay a donde postear y el submit nativo esta cortado:
        // mejor avisar que tragarse el mensaje del visitante en silencio.
        this.setStatus(this.dataset.error || 'No pudimos enviar el mensaje.', true);
        return;
      }

      if (!this.validate()) return;

      if (this.submitBtn) this.submitBtn.setAttribute('disabled', 'true');

      var payload = Object.fromEntries(new FormData(this.form));

      try {
        var res = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        var data = await res.json();

        if (res.ok && data && data.success) {
          this.setStatus(this.dataset.success || '¡Gracias! Recibimos tu mensaje.', false);
          this.form.reset();
          // El boton queda deshabilitado tras el exito, como en el tema
          // publicado: evita el doble envio del que vuelve a tocar Enviar.
          return;
        }

        throw new Error('mailer rejected the submission');
      } catch (error) {
        this.setStatus(this.dataset.error || 'Ocurrió un error al enviar el mensaje, por favor intentá nuevamente.', true);
        if (this.submitBtn) this.submitBtn.removeAttribute('disabled');
      }
    }
  }

  customElements.define('ig-form-mailer', IGFormMailer);
})();
